import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarCheck } from "lucide-react";
import { useApp } from "@/state/AppContext";
import { useRequireAuth } from "@/lib/useRequireAuth";
import { api, apiErrorMessage } from "@/lib/api";
import { validatePhone } from "@/lib/phone";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { localTime, businessDay, addDays } from "@/lib/format";

const today = () => businessDay();

/** Storefront S5 (gap review item 8): the next 4 calendar days as `{value, weekday, day}` — the
 * handoff picks a day with 4 "TUE 23" chips instead of a native `<input type="date">`
 * (`Restaurant Website.dc.html` RESERVE A TABLE). Built from `Date`, not invented — always starts
 * today, on the IST business calendar (the same day the server books against). */
function nextDays(t, count = 4) {
  const short = t("storefront.slots.weekdayShort");
  const out = [];
  for (let i = 0; i < count; i++) {
    const value = addDays(businessDay(), i);
    const d = new Date(`${value}T12:00:00Z`);
    // JS getUTCDay(): Sunday=0..Saturday=6 -> this codebase's Monday=0..Sunday=6 convention.
    const weekday = (d.getUTCDay() + 6) % 7;
    out.push({ value, label: short[weekday], day: d.getUTCDate() });
  }
  return out;
}

export function TableSection({ business }) {
  const { t, lang, auth } = useApp();
  const requireAuth = useRequireAuth();
  const days = nextDays(t);
  const [date, setDate] = useState(days[0].value);
  const [data, setData] = useState(null);
  const [slot, setSlot] = useState("");
  const [form, setForm] = useState({ party_size: 2, guest_name: "", guest_phone: "", notes: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setSlot("");
    try {
      // The route's query param is `day` (IST calendar day), not `date` — sending `date` was
      // silently ignored, so the picker always showed today's grid (the F-07 doctors class).
      const res = await api.get(`/businesses/${business.id}/table-slots`, { params: { day: date } });
      setData(res.data);
    } catch (e) {
      setData({ slots: [] });
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [business.id, date]);

  const reserve = async () => {
    // One up-front convention (L2): the shared sign-in prompt, which returns here after login.
    if (!requireAuth()) return;
    // R11 PHONE: the same rule the server applies, in the visitor's language, before the round trip.
    const phoneCheck = validatePhone(form.guest_phone, "mobile");
    if (!phoneCheck.ok) {
      toast.error(t(phoneCheck.messageKey));
      return;
    }
    setBusy(true);
    try {
      await api.post("/table-reservations", {
        business_id: business.id,
        slot_start_at: slot,
        ...form,
        party_size: Number(form.party_size) || 1,
      });
      toast.success(t("tables.booked"));
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e, t("errors.generic")));
    } finally {
      setBusy(false);
    }
  };

  const slots = data?.slots || [];
  const chosenDay = days.find((d) => d.value === date);
  const chosenSlotLabel = slot ? localTime(slot, lang) : "";
  const reserveLabel =
    slot && chosenDay
      ? t("tables.reserveSummary", {
          day: `${chosenDay.label} ${chosenDay.day}`,
          time: chosenSlotLabel,
          count: Number(form.party_size) || 1,
        })
      : auth
        ? t("tables.book")
        : t("tables.signInToBook");

  return (
    <div className="overflow-hidden rounded-[22px] border border-border bg-background" data-testid="table-section">
      {/* Storefront S5 (gap review item 8): dark header band — icon, title, "Free · instant"
          pill, helper line — matching the handoff card pattern instead of a plain heading. */}
      <div className="bg-[hsl(var(--brand-deep))] px-6 py-5">
        <div className="flex flex-wrap items-center gap-2.5">
          <span className="grid h-8 w-8 shrink-0 place-items-center rounded-[10px] bg-white/15 text-white">
            <CalendarCheck size={17} />
          </span>
          <h2 className="font-heading text-lg font-bold text-white">{t("tables.reserve")}</h2>
          <span className="ml-auto rounded-full bg-[hsl(var(--accent))] px-3 py-1 text-xs font-bold text-[hsl(var(--accent-ink))]">
            {t("tables.freeInstant")}
          </span>
        </div>
        <p className="mt-2.5 max-w-[60ch] text-sm text-white/75">{t("tables.holdNotice")}</p>
      </div>

      <div className="space-y-5 p-6">
        {data?.max_party_size && (
          <p className="text-xs text-muted-foreground">{t("tables.maxParty", { count: data.max_party_size })}</p>
        )}

        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{t("tables.selectDate")}</p>
          <div className="mt-2.5 flex gap-2.5 overflow-x-auto pb-0.5" data-testid="table-day-list">
            {days.map((d) => (
              <button
                key={d.value}
                type="button"
                onClick={() => setDate(d.value)}
                data-testid={`table-day-${d.value}`}
                className={`shrink-0 rounded-2xl border px-3.5 py-2.5 text-center transition-colors duration-200 ${
                  date === d.value
                    ? "border-2 border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/[.06]"
                    : "border-border bg-background"
                }`}
              >
                <div className={`text-[11px] font-bold ${date === d.value ? "text-[hsl(var(--primary))]" : "text-muted-foreground"}`}>
                  {d.label}
                </div>
                <div className={`font-heading tabular-nums text-lg font-extrabold ${date === d.value ? "text-[hsl(var(--primary))]" : ""}`}>
                  {d.day}
                </div>
              </button>
            ))}
          </div>
        </div>

        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{t("tables.selectSlot")}</p>
          {slots.filter((s) => s.available).length === 0 ? (
            <p className="mt-2 text-sm text-muted-foreground" data-testid="no-table-slots">
              {t("tables.noSlots")}
            </p>
          ) : (
            <div className="mt-2.5 grid grid-cols-3 gap-2 sm:grid-cols-4" data-testid="table-slot-list">
              {slots.map((s) => (
                <button
                  key={s.start_at}
                  disabled={!s.available}
                  onClick={() => setSlot(s.start_at)}
                  data-testid={`table-slot-${s.start_at}`}
                  className={`rounded-xl border px-2 py-2.5 text-center text-sm font-semibold transition-colors duration-200 ${
                    slot === s.start_at
                      ? "border-2 border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/[.06] text-[hsl(var(--primary))]"
                      : s.available
                        ? "border-border"
                        : "border-dashed border-border text-muted-foreground/50"
                  }`}
                >
                  <span className="tabular-nums">{localTime(s.start_at, lang)}</span>
                  <span className="mt-0.5 block text-[10px] font-normal">{t("tables.tablesLeft", { count: s.tables_left })}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {slot && (
          <div className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="table-guest-name">{t("tables.guestName")}</Label>
                <Input
                  id="table-guest-name"
                  data-testid="table-guest-name-input"
                  value={form.guest_name}
                  onChange={(e) => setForm({ ...form, guest_name: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="party-size">{t("tables.partySize")}</Label>
                <Input
                  id="party-size"
                  type="number"
                  min={1}
                  data-testid="party-size-input"
                  value={form.party_size}
                  onChange={(e) => setForm({ ...form, party_size: e.target.value })}
                />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="table-guest-phone">{t("tables.guestPhone")}</Label>
                <Input
                  id="table-guest-phone"
                  data-testid="table-guest-phone-input"
                  value={form.guest_phone}
                  onChange={(e) => setForm({ ...form, guest_phone: e.target.value })}
                />
              </div>
            </div>
            <Button
              className="w-full rounded-xl py-6 text-[15px] font-bold"
              data-testid="reserve-table-btn"
              disabled={busy}
              onClick={reserve}
            >
              {busy ? t("common.saving") : reserveLabel}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
